import React, { useEffect, useState, useMemo, useRef } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { getErrorMessage } from "utils";

import { useSnackbar } from "contexts";
import {
  useCreatePaymentMethodSetupIntent,
  useSetDefaultPaymentMethod,
} from "hooks/api";

import {
  Stack,
  Typography,
  useTheme,
  InputBase,
} from "@mui/material";

import { Button, Modal, Skeleton } from "@repo/ui";
import { CheckboxField } from "components";
import { CreditCardField, creditCardSurfaceSx } from "components/input";
import { STRIPE_PUBLIC_KEY } from "config";

interface CreatePaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  refetch: () => Promise<unknown> | void;
}

export const CreatePaymentMethodModal = (
  props: CreatePaymentMethodModalProps
) => {
  const { open, onClose = () => {}, refetch } = props;
  const { open: notification } = useSnackbar();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [saveAsDefault, setSaveAsDefault] = useState<boolean>(true);
  const [cardholderName, setCardholderName] = useState("");

  const { mutateAsync: createSetupIntent } =
    useCreatePaymentMethodSetupIntent();
  const setDefaultPaymentMethod = useSetDefaultPaymentMethod();

  const stripePromise = useMemo(
    () => (STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY as string) : null),
    []
  );

  const notificationRef = useRef(notification);
  const onCloseRef = useRef(onClose);
  const createSetupIntentRef = useRef(createSetupIntent);

  useEffect(() => {
    notificationRef.current = notification;
  }, [notification]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    createSetupIntentRef.current = createSetupIntent;
  }, [createSetupIntent]);

  useEffect(() => {
    let isMounted = true;
    const bootstrap = async () => {
      setClientSecret(null);
      setSaveAsDefault(true);
      setCardholderName("");
      try {
        const data = await createSetupIntentRef.current();
        if (isMounted) {
          setClientSecret(data.clientSecret);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        notificationRef.current({
          type: "error",
          title: message || "Failed to start payment method setup",
        });
        if (isMounted) {
          onCloseRef.current();
        }
      }
    };

    if (open && stripePromise) {
      bootstrap();
    } else if (open && !stripePromise) {
      // VRG-607 — was "Stripe public key is not configured". That describes OUR
      // deployment config to a brand who can do nothing about it, and names a
      // third-party SDK besides. It is a real misconfiguration, so it still gets
      // reported — to the console, where an engineer will see it. The user gets
      // the state and the exit.
      console.error(
        "[billing] Stripe publishable key missing — card entry cannot mount for this build.",
      );
      notificationRef.current({
        type: "error",
        title: "Card payments are unavailable right now",
        text: "We can't add a payment method at the moment. Please try again shortly, or contact support if it continues.",
      });
    }

    return () => {
      isMounted = false;
      setClientSecret(null);
    };
  }, [open, stripePromise]);

  const handleClose = () => {
    onClose();
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <Modal
      title="Add new payment method"
      open={open}
      onClose={handleClose}
      actions={
        clientSecret && stripePromise ? (
          <>
            <Button
              disableElevation
              variant="secondary"
              onClick={() => handleClose()}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              type="submit"
              form="card-setup-form"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving…" : "Add"}
            </Button>
          </>
        ) : undefined
      }
    >
      {!stripePromise ? (
        <Stack spacing={4} alignItems="center" justifyContent="center" py={6}>
          <Typography variant="body2" color="text.secondary">
            Stripe is not configured. Please set the Stripe public key.
          </Typography>
        </Stack>
      ) : clientSecret ? (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CardSetupForm
            clientSecret={clientSecret}
            onClose={handleClose}
            refetch={refetch}
            notify={notification}
            saveAsDefault={saveAsDefault}
            setSaveAsDefault={setSaveAsDefault}
            cardholderName={cardholderName}
            setCardholderName={setCardholderName}
            setDefaultPaymentMethod={async (paymentMethodId) => {
              await setDefaultPaymentMethod.mutateAsync(paymentMethodId);
            }}
          />
        </Elements>
      ) : (
        // Skeleton mirroring the form 1:1 (§19) so the fields load in the SAME
        // place — two labelled 49px fields (matching the real field height) + the
        // "Save as default" checkbox row (square + label). Not a centered spinner.
        <Stack spacing={4}>
          <Stack spacing={2}>
            <Skeleton width={80} height={18} />
            <Skeleton height={49} />
          </Stack>
          <Stack spacing={2}>
            <Skeleton width={90} height={18} />
            <Skeleton height={49} />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton width={20} height={20} />
            <Skeleton width={180} height={16} />
          </Stack>
        </Stack>
      )}
    </Modal>
  );
};

type CardSetupFormProps = {
  clientSecret: string;
  onClose: () => void;
  refetch: () => Promise<unknown> | void;
  notify: (payload: {
    type: "success" | "error";
    title: string;
    text?: string;
  }) => void;
  saveAsDefault: boolean;
  setSaveAsDefault: (value: boolean) => void;
  cardholderName: string;
  setCardholderName: (value: string) => void;
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>;
};

const CardSetupForm = ({
  clientSecret,
  onClose,
  refetch,
  notify,
  saveAsDefault,
  setSaveAsDefault,
  cardholderName,
  setCardholderName,
  setDefaultPaymentMethod,
}: CardSetupFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();

  const fieldContainerStyles = useMemo(() => creditCardSurfaceSx(theme), [theme]);

  const stripeElementOptions = useMemo(
    () => ({
      style: {
        base: {
          // Stripe's 16px renders visibly larger than a native 16px input, so it
          // read bigger than the "Name on card" field. 14px matches it visually.
          fontSize: "14px",
          color: theme.palette.text.primary,
          fontFamily: theme.typography.fontFamily,
          "::placeholder": {
            color: theme.palette.text.disabled,
          },
        },
        invalid: {
          color: theme.palette.error.main,
        },
      },
    }),
    [
      theme.palette.error.main,
      theme.palette.text.disabled,
      theme.palette.text.primary,
      theme.typography.fontFamily,
    ]
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      // VRG-607 — "Card element could not be initialized" is Stripe SDK
      // vocabulary ("Element" is their name for the hosted card iframe). The
      // reader has no such word and cannot act on it. What they CAN do is reload
      // and re-enter the card, so that is what the copy says.
      console.error("[billing] Stripe CardElement not mounted at submit time.");
      notify({
        type: "error",
        title: "We couldn't read your card details",
        text: "Please reload the page and enter your card again.",
      });
      setIsSubmitting(false);
      return;
    }

    const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: cardholderName || undefined,
        },
      },
    });

    if (error) {
      notify({
        type: "error",
        title: error.message || "Failed to add payment method",
      });
      setIsSubmitting(false);
      return;
    }

    const paymentMethodId =
      typeof setupIntent.payment_method === "string"
        ? setupIntent.payment_method
        : setupIntent.payment_method?.id;

    try {
      // No success toast: the refetched list shows the new card (and the Default
      // badge moves to it when saveAsDefault was checked).
      if (saveAsDefault && paymentMethodId) {
        await setDefaultPaymentMethod(paymentMethodId);
      }

      await Promise.resolve(refetch());
      onClose();
    } catch (error) {
      const message = getErrorMessage(error);
      notify({
        type: "error",
        title: message || "Failed to set default payment method",
      });
      await Promise.resolve(refetch());
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} id="card-setup-form">
      <Stack spacing={4}>
        <Stack spacing={2}>
          <Typography variant="body3" fontWeight={500}>
            Credit card
          </Typography>
          <CreditCardField options={stripeElementOptions} sx={fieldContainerStyles} />
        </Stack>
        <Stack spacing={2}>
          <Typography variant="body3" fontWeight={500}>
            Name on card
          </Typography>
          <InputBase
            placeholder="Full name"
            value={cardholderName}
            onChange={(event) => setCardholderName(event.target.value)}
            fullWidth
            sx={{
              ...fieldContainerStyles,
              "& .MuiInputBase-input": {
                padding: 0,
                fontSize: 16,
                color: theme.palette.text.primary,
                // Match the Stripe placeholder exactly (solid text.disabled at
                // 16px) — MUI's default placeholder is faded, which read as a
                // different size/weight next to the card field.
                "&::placeholder": {
                  color: theme.palette.text.disabled,
                  opacity: 1,
                  fontSize: 16,
                },
              },
            }}
          />
        </Stack>
        <CheckboxField
          name="default-payment-method"
          label="Save as default payment method"
          checked={saveAsDefault}
          onChange={() => setSaveAsDefault((prev) => !prev)}
        />
      </Stack>
    </form>
  );
};
